import {
  lazy,
  object,
  string,
  number,
  addMethod,
  StringSchema as YupStringSchema,
} from "yup";
import { ValidationErrors } from "final-form";
import parsePhoneNumber, { CountryCode } from "libphonenumber-js";
import { AddressDetailsFormValues } from "v3/types/address";
import { PaymentFormValues } from "v3/types/payment";
import { ReactSelectDropdownOption } from "@pepdirect/v3/types";
import { GiftFormValues } from "v3/components/forms/GiftForm";
import {
  invalidZipCaMsg,
  invalidZipPlMsg,
  invalidZipUsMsg,
  regexEmailHelper,
  regexZipCa,
  regexZipPl,
  regexZipUs,
  validateWithYup,
} from "@pepdirect/v3/helpers/validation";
import {
  FormsAddressLocalization,
  FormsValidationLocalization,
} from "v3/services/graphql/generated";
import { getLocalizedValidationMessage } from "@pepdirect/v3/helpers/localization";

const FormErrorsType = Promise;
type FormErrorsType = Promise<ValidationErrors | undefined>;

export interface AddressValidationProps {
  shippingAddress: AddressDetailsFormValues;
}
export interface ShippingAddressFormValues {
  shippingAddress: AddressDetailsFormValues;
  giftInfo?: GiftFormValues;
  additionalPhoneNumber?: string;
}

export interface PaymentAndBillingFormValues {
  payment: PaymentFormValues;
  billingAddress?: AddressDetailsFormValues;
  poNumber?: string;
}

interface AddressSchema {
  isStateRequired?: boolean;
  showTaxId?: boolean;
  showCompany?: boolean | null;
  country?: string;
  localization?: FormsValidationLocalization | null;
  addressLocalization?: FormsAddressLocalization | null;
}

declare module "yup" {
  interface StringSchema {
    phone(message?: string, country?: string): YupStringSchema;
  }
}

export const validatePhoneNumber = (value: string, country?: CountryCode) => {
  // we're parsing phone number because it strips and removes all spaces, dashes
  // and brackets and format like "1234123412" in nationalNumber making
  // it easy to do following validation
  const parsedNumber = parsePhoneNumber(value, country);
  if (parsedNumber) {
    const { nationalNumber, countryCallingCode, country } = parsedNumber;
    let isPossiblePhoneNumber = parsedNumber.isPossible();

    // hardcoding length for these 2 countries to match the BE logic
    if (country === "PL") {
      isPossiblePhoneNumber = nationalNumber.length === 9;
    } else if (country === "US") {
      isPossiblePhoneNumber = nationalNumber.length === 10;
    }

    // isPossible is more flexible but also allows 0's
    // we're checking here to make sure it's not all 0's
    const isAllZeros = /^0*$/.test(nationalNumber);
    if (isAllZeros) {
      return false;
    }

    // must be without conutry code
    if (value.startsWith(`+${countryCallingCode}`)) {
      return false;
    }

    return isPossiblePhoneNumber;
  }
  return false;
};

addMethod(string, "phone", function (message: string, country?: CountryCode) {
  return this.test("phone", message, function (value) {
    const { path, createError } = this;
    if (!value) {
      return true;
    }
    const isValidPhoneNumber = validatePhoneNumber(value, country);

    if (!isValidPhoneNumber) {
      return createError({ path, message: message });
    }
    return true;
  });
});

const getAddressPartial = ({
  isStateRequired,
  localization,
  showTaxId,
  showCompany,
  addressLocalization,
}: AddressSchema) => {
  const fieldRequiredMsg = getLocalizedValidationMessage(
    "fieldRequiredErrorText",
    localization
  );

  const { companyLabel = "Company", taxIdLabel = "Tax ID" } =
    addressLocalization ?? {};

  return {
    firstName: string().required(fieldRequiredMsg).nullable(),
    lastName: string().required(fieldRequiredMsg).nullable(),
    line1: lazy((val: Record<string, unknown> | string) => {
      // type varies depending on autocomplete feature
      if (typeof val === "object") {
        return object()
          .shape({
            id: string(),
            value: string(),
            type: string(),
            label: string(),
          })
          .nullable();
      } else {
        return string().required(fieldRequiredMsg).nullable();
      }
    }),
    city: string().required(fieldRequiredMsg).nullable(),
    ...(isStateRequired
      ? {
          state: object()
            .shape({
              value: string().required(),
              label: string().required(),
            })
            // https://github.com/jquense/yup/issues/1455#issuecomment-1318661574
            .default(undefined)
            .required(fieldRequiredMsg)
            .nullable(),
        }
      : {}),
    zip: string()
      .when("country", {
        is: (option: ReactSelectDropdownOption) => option?.value === "US",
        then: string()
          .matches(regexZipUs, {
            excludeEmptyString: true,
            message: invalidZipUsMsg,
          })
          .required(fieldRequiredMsg)
          .nullable(),
      })
      .when("country", {
        is: (option: ReactSelectDropdownOption) => option?.value === "CA",
        then: string()
          .matches(regexZipCa, {
            excludeEmptyString: true,
            message: invalidZipCaMsg,
          })
          .required(fieldRequiredMsg)
          .nullable(),
      })
      .when("country", {
        is: (option: ReactSelectDropdownOption) => option?.value === "PL",
        then: string()
          .matches(regexZipPl, {
            excludeEmptyString: true,
            message: invalidZipPlMsg,
          })
          .required(fieldRequiredMsg)
          .nullable(),
      })
      .when("country", {
        is: (option: ReactSelectDropdownOption) =>
          !["US", "CA", "PL"].includes(option?.value),
        then: string().required(fieldRequiredMsg).nullable(),
      }),
    country: object()
      .shape({
        value: string().required(),
        label: string().required(),
      })
      .default(undefined)
      .required(fieldRequiredMsg)
      .nullable(),
    ...(showTaxId
      ? {
          taxId: number()
            .typeError(
              getLocalizedValidationMessage(
                "invalidTaxIdErrorText",
                localization
              )
            )
            .test(
              "len",
              getLocalizedValidationMessage(
                "invalidTaxIdLengthErrorText",
                localization
              ),
              (val) => {
                if (!val) return true; // can be empty
                return val?.toString().length === 10;
              }
            )
            .test(
              "taxIdRequiredWithCompany",
              getLocalizedValidationMessage(
                "taxIdRequiredWithCompanyErrorText",
                localization,
                { companyLabel, taxIdLabel }
              ),
              (val, context) => {
                if (showCompany && showTaxId && !val && context.parent.company)
                  return false;
                return true;
              }
            )
            .nullable(),
        }
      : {}),

    ...(showCompany
      ? {
          company: string()
            .test(
              "companyRequiredWithTaxId",
              getLocalizedValidationMessage(
                "companyRequiredWithTaxIdErrorText",
                localization,
                { companyLabel, taxIdLabel }
              ),
              (val, context) => {
                if (showCompany && showTaxId && !val && context.parent.taxId)
                  return false;
                return true;
              }
            )
            .nullable(),
        }
      : {}),
  };
};

export const passwordResetSchema = ({ localization }: AddressSchema) =>
  object({
    password: string()
      .trim()
      .required(
        getLocalizedValidationMessage(
          "newPasswordRequiredErrorText",
          localization
        )
      ),
  });

const getCardholderNameSchema = ({ localization }: AddressSchema) =>
  string().required(
    getLocalizedValidationMessage("fieldRequiredErrorText", localization)
  );

const getPhoneSchema = ({ localization, country }: AddressSchema) =>
  string()
    .phone(
      getLocalizedValidationMessage("invalidPhoneErrorText", localization),
      country
    )
    .required(
      getLocalizedValidationMessage("fieldRequiredErrorText", localization)
    )
    .nullable();

const getEmailSchema = ({ localization }: AddressSchema) => {
  const wrongEmailErrorText = getLocalizedValidationMessage(
    "wrongEmailErrorText",
    localization
  );
  return string()
    .trim()
    .email(wrongEmailErrorText)
    .matches(regexEmailHelper, {
      excludeEmptyString: true,
      message: wrongEmailErrorText,
    })
    .required(
      getLocalizedValidationMessage("fieldRequiredErrorText", localization)
    )
    .nullable();
};

export const getUserShipSchema = ({
  isStateRequired,
  showTaxId,
  localization,
  country,
  showCompany,
  addressLocalization,
}: AddressSchema) =>
  object({
    shippingAddress: object({
      ...getAddressPartial({
        isStateRequired,
        localization,
        showTaxId,
        showCompany,
        addressLocalization,
      }),
      nickname: string().max(140).nullable(),
      phone: getPhoneSchema({ localization, country }),
    }),
  });

const getGuestShipSchema = ({
  isStateRequired,
  showTaxId,
  localization,
  country,
  showCompany,
  addressLocalization,
}: AddressSchema) =>
  object({
    shippingAddress: object({
      email: getEmailSchema({ localization }),
      ...getAddressPartial({
        isStateRequired,
        localization,
        showTaxId,
        showCompany,
        addressLocalization,
      }),
      nickname: string().max(140).nullable(),
      phone: getPhoneSchema({ localization, country }),
    }),
  });

export const getShippingPhoneNumberSchema = ({
  localization,
  country,
}: AddressSchema) =>
  object({
    shippingOptionPhoneNumber: object({
      phone: getPhoneSchema({ localization, country }),
    }),
  });

export const getAdditionalPhoneNumberSchema = ({
  localization,
  country,
}: AddressSchema) =>
  object({
    additionalPhoneNumber: string()
      .phone(
        getLocalizedValidationMessage("invalidPhoneErrorText", localization),
        country
      )
      .nullable(),
  });

const getGiftSchema = ({ localization }: AddressSchema) =>
  object({
    giftInfo: object().shape({
      gifteeEmail: getEmailSchema({ localization }),
      gifteeMessage: string().nullable(),
      giftorName: string()
        .required(
          getLocalizedValidationMessage("fieldRequiredErrorText", localization)
        )
        .nullable(),
    }),
  });

const getUserPaymentSchema = ({ localization }: AddressSchema) =>
  object({
    payment: object({
      cardholderName: getCardholderNameSchema({ localization }),
    }),
  });

const getUserPaymentAndBillSchema = ({
  isStateRequired,
  localization,
}: AddressSchema) =>
  object({
    payment: object({
      cardholderName: getCardholderNameSchema({ localization }),
    }),
    billingAddress: object({
      ...getAddressPartial({ isStateRequired, localization }),
    }),
  });

interface ShippingAddressValidation {
  isGiftFormOpen?: boolean;
  isGuest?: boolean;
  isNewShipFormOpen?: boolean;
  isStateRequired: boolean;
  showTaxId?: boolean;
  localization?: FormsValidationLocalization | null;
  values: ShippingAddressFormValues;
  country: string;
  showCompany?: boolean | null;
  addressLocalization?: FormsAddressLocalization | null;
}

export const shippingAddressValidation = async ({
  isGiftFormOpen,
  isGuest,
  isNewShipFormOpen,
  isStateRequired,
  showTaxId,
  localization,
  values,
  country,
  showCompany,
  addressLocalization,
}: ShippingAddressValidation): FormErrorsType => {
  const shipSchema = isGuest
    ? getGuestShipSchema({
        isStateRequired,
        showTaxId,
        showCompany,
        localization,
        country,
        addressLocalization,
      })
    : getUserShipSchema({
        isStateRequired,
        showTaxId,
        showCompany,
        localization,
        country,
        addressLocalization,
      });
  const giftSchema = getGiftSchema({ localization });

  // nothing open
  if (!isNewShipFormOpen && !isGiftFormOpen) return {};
  // both ship and gift form open
  if (isGiftFormOpen && isNewShipFormOpen) {
    return await validateWithYup(giftSchema.concat(shipSchema))(values);
  }
  // gift form open only
  if (isGiftFormOpen && !isNewShipFormOpen) {
    return await validateWithYup(giftSchema)(values);
  }
  // ship form open only
  if (!isGiftFormOpen && isNewShipFormOpen) {
    return await validateWithYup(shipSchema)(values);
  }
};

export const additionalPhoneNumberValidation = async ({
  values,
  localization,
  country,
}: Omit<ShippingAddressValidation, "isStateRequired">): FormErrorsType => {
  return await validateWithYup(
    getAdditionalPhoneNumberSchema({ localization, country })
  )(values);
};

interface PaymentValidation {
  values: PaymentAndBillingFormValues;
  isBillingEqualShipping?: boolean;
  isStateRequired: boolean;
  localization?: FormsValidationLocalization | null;
}

export const paymentValidation = async ({
  values,
  isBillingEqualShipping,
  isStateRequired,
  localization,
}: PaymentValidation): FormErrorsType => {
  return await validateWithYup(
    isBillingEqualShipping
      ? getUserPaymentSchema({ localization })
      : getUserPaymentAndBillSchema({ isStateRequired, localization })
  )(values);
};
